<template>
  <div class="level-item has-text-centered">
    <div>
      <p class="heading">Tempo</p>
      <p class="title has-text-warning">{{ timer }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { relogio } from '../resources/ofertasCompra';

export default {
  name: 'AppTimer',
  data() {
    return {
      timer: '00:00',
      remainingSeconds: 0,
      intervalId: null,
      endTimestamp: null,
      ofertaCompraId: null,
    };
  },
  computed: {
    ...mapGetters({
      auction: 'obterOfertaCompra',
      items: 'obterTodosItens',
    }),

    randomTime() {
      return this.items.some(item => item.estado === 'LANCE-RANDOMICO');
    },
    itemsCarregados() {
      return Array.isArray(this.items) && this.items.length > 0;
    },
  },
  methods: {
    async initTimer() {
      const isManifestacaoIntencao =
        Number(this.auction?.situacao?.id || this.auction?.situacaoId) === 20;

      if (!this.itemsCarregados && !isManifestacaoIntencao) return;

      if (this.randomTime) {
        this.limparStorage();
        this.remainingSeconds = 0;
        this.timer = this.formatTime(0);
        this.atualizarTitulo();
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
        return;
      }

      this.restaurarSeExistir();

      if (!this.intervalId && (this.ofertaCompraId || this.auction.id)) {
        const { data } = await relogio(this.ofertaCompraId ?? this.auction.id);

        if (data?.data?.ativo && Number.isFinite(data?.data.tempoRestante)) {
          this.iniciarIntervalo(data.data.tempoRestante);
        }
      }
    },
    formatTime(seconds) {
      const total = Math.max(0, seconds);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = n => (n < 10 ? `0${n}` : `${n}`);
      if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
      return `${pad(m)}:${pad(s)}`;
    },
    atualizarTitulo() {
      const isManifestacaoIntencao =
        Number(this.auction?.situacao?.id || this.auction?.situacaoId) === 20;
      if (
        this.remainingSeconds > 0 &&
        (this.itemsCarregados || isManifestacaoIntencao) &&
        !this.randomTime
      ) {
        document.title = `${this.timer} SelCorp - Sistema Eletrônico de Licitações`;
      } else {
        document.title = 'SelCorp - Sistema Eletrônico de Licitações';
      }
    },
    storageKey() {
      if (!this.ofertaCompraId && !this.auction.id) return null;
      return `auctionTimerEnd_${this.ofertaCompraId ?? this.auction.id}`;
    },
    salvarFimNoStorage() {
      const key = this.storageKey();
      if (key && this.endTimestamp) {
        localStorage.setItem(key, String(this.endTimestamp));
      }
    },
    limparStorage() {
      const key = this.storageKey();
      if (key) {
        localStorage.removeItem(key);
      }
    },
    iniciarIntervalo(totalSeconds) {
      if (!this.ofertaCompraId && !this.auction.id) return;

      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      this.endTimestamp = Date.now() + totalSeconds * 1000;
      this.salvarFimNoStorage();

      this.remainingSeconds = totalSeconds;
      this.timer = this.formatTime(totalSeconds);
      this.atualizarTitulo();

      this.intervalId = setInterval(() => {
        const diff = this.endTimestamp - Date.now();
        const left = Math.floor(diff / 1000);

        if (left <= 0) {
          this.remainingSeconds = 0;
          this.timer = this.formatTime(0);
          this.atualizarTitulo();
          this.limparStorage();
          clearInterval(this.intervalId);
          this.intervalId = null;
          this.$emitBus('onTerminar:itensEmLance');
          return;
        }

        this.remainingSeconds = left;
        this.timer = this.formatTime(left);
        this.atualizarTitulo();
      }, 1000);
    },
    restaurarSeExistir() {
      const isManifestacaoIntencao =
        Number(this.auction?.situacao?.id || this.auction?.situacaoId) === 20;

      if ((!Array.isArray(this.items) || this.items.length === 0) && !isManifestacaoIntencao) {
        return;
      }

      if (this.randomTime) {
        this.limparStorage();
        this.atualizarTitulo();
        return;
      }

      if (!this.ofertaCompraId && !this.auction.id) return;

      const key = this.storageKey();
      const stored = localStorage.getItem(key);
      if (!stored) return;

      const end = Number(stored);
      if (!Number.isFinite(end)) return;

      const diff = end - Date.now();
      const left = Math.floor(diff / 1000);

      if (left <= 0) {
        this.timer = this.formatTime(0);
        this.limparStorage();
        return;
      }

      this.endTimestamp = end;
      this.iniciarIntervalo(left);
    },
  },
  sockets: {
    relogio_iniciado(payload) {
      const { tempo, ofertaCompraId } = payload;

      if (!Number.isFinite(tempo) || (!ofertaCompraId && !this.auction.id)) return;

      this.ofertaCompraId = ofertaCompraId ?? this.auction.id;

      this.iniciarIntervalo(Number(tempo));
    },
  },
  async created() {
    this.restaurarSeExistir();
  },
  watch: {
    itemsCarregados: {
      immediate: true,
      handler(loaded) {
        if (loaded) {
          this.initTimer();
        } else {
          this.atualizarTitulo();
        }
      },
    },

    randomTime(newVal) {
      if (newVal) {
        this.initTimer();
      } else if (this.itemsCarregados) {
        this.initTimer();
      }
    },

    ofertaCompraId(newVal) {
      if (newVal && this.itemsCarregados) {
        this.initTimer();
      }
    },

    'auction.situacao.id': {
      immediate: true,
      handler(situacaoId) {
        if (Number(situacaoId) === 20) {
          this.initTimer();
        }
      },
    },
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
};
</script>
